/**
 * SpaceScene.ts — World 2 space shoot-em-up scene controller.
 *
 * Lifecycle:
 *  - `init()`  — resets space state, clears the physics accumulator, registers
 *                {@link stepSpace} as the active fixed-step on the scene-step
 *                registry, and flushes any latched input from a prior session.
 *  - `destroy()` — restores the default platformer step, drops the ship and
 *                  camera, and clears input. Safe to call multiple times.
 *
 * This is the minimal scaffold needed by the engine-prep refactor. The
 * ship and ship-combat (projectile firing + culling) and asteroid (spawner,
 * drift, collision) lifecycles are wired here. Renderer integration,
 * ship-asteroid contact damage, the enemy update list, and the boss trigger
 * land in subsequent PRs.
 *
 * NO React imports, NO Skia imports. Orchestrates writes to `spaceState`.
 *
 * Phase 4 — World 2: Starfield Drift.
 * See ADR-009 and ROADMAP.md Phase 4 "Space Engine".
 *
 * @module SpaceScene
 */

import { initSpaceState, clearSpaceState, spaceState } from '../engine/spaceState';
import { resetAccumulator } from '../engine/physicsFrame';
import { setActiveStep } from '../engine/sceneStep';
import { stepSpace } from '../engine/spaceStep';
import { resetShipCombat } from '../engine/shipCombat';
import { resetAsteroids } from '../engine/asteroidSystem';
import { resetSpaceEnemies } from '../engine/spaceEnemySystem';
import { resetWaveScheduler } from '../engine/waveScheduler';
import { resetBoss } from '../engine/bossSystem';
import { releaseAllFighterDroneLasers } from '../engine/fighterDroneFire';
import { releaseAllBomberOrdnance } from '../engine/bomberDroneDrop';
import { resetHomingMines } from '../engine/homingMineSpawner';
import {
  resetSpaceShards,
  spawnShardsFromLevel,
} from '../engine/spaceShardSystem';
import { resetSpaceLoot } from '../engine/spaceLootSystem';
import { resetExplosionVfx } from '../engine/explosionVfx';
import { updateSpaceHudSnapshot } from '../engine/spaceHudSnapshot';
import { gameState } from '../engine/gameState';
import { ShipEntity } from '../entities/ShipEntity';
import { SHIP_WIDTH, SHIP_HEIGHT } from '../../constants/spaceConstants';
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import { clearAllInputState } from '../input/inputState';
import { SPACE_LEVEL_DEFAULT, type SpaceLevelConfig } from '../levels/spaceLevels';

/**
 * SpaceScene — lifecycle controller for one space-shmup level session.
 * Call `init()` before the first frame and `destroy()` on unmount.
 */
export class SpaceScene {
  /** True between init() and destroy(). */
  private _active = false;

  /**
   * Initialise the space scene.
   *
   * DECISION: Clears input state BEFORE touching the registry so a key held
   * over the level transition (e.g. the menu/world-map "Enter" key) does not
   * leak into the first physics tick and immediately start moving the ship.
   * Mirrors the platformer `PlayScene.init()` sequencing.
   *
   * @param config - Per-level gating + end-condition config. Defaults to
   *   {@link SPACE_LEVEL_DEFAULT} (everything enabled, never ends) so any
   *   call site that hasn't migrated yet keeps its original behaviour.
   */
  public init(config: SpaceLevelConfig = SPACE_LEVEL_DEFAULT): void {
    clearAllInputState();
    initSpaceState();
    spaceState.levelConfig = config;
    // Release any pooled projectiles left over from a prior session and zero
    // the cooldown. Owns these because the pool lives in shipCombat, not spaceState.
    resetShipCombat();
    // Same contract for asteroids — the pool lives in asteroidSystem.
    resetAsteroids();
    // Same contract for space enemies — the pool lives in spaceEnemySystem.
    resetSpaceEnemies();
    // Wave scheduler holds a module-local cursor into the level's waveScript;
    // reset it so each scene lifetime starts at slot 0.
    resetWaveScheduler();
    // Same contract for the boss — its singleton instance lives in bossSystem.
    resetBoss();
    // Release any fighter-drone lasers left over from a prior session. The
    // pool lives in fighterDroneFire, parallel to the boss-bolt pool.
    releaseAllFighterDroneLasers();
    // Same contract for bomber ordnance — the cluster-bomb and shrapnel
    // pools both live in bomberDroneDrop.
    releaseAllBomberOrdnance();
    // Same contract for homing mines — the pool lives in homingMineSpawner.
    // The spawner also self-clears via its internal sync() watcher, but
    // releasing here keeps the contract symmetric with the other systems and
    // guarantees pool capacity is available the moment init() returns.
    resetHomingMines();
    // Release any shards left over from a prior session, then spawn
    // the active level's placements (no-op when `config.shards` is
    // undefined, e.g. boss arena and the legacy default config).
    resetSpaceShards();
    spawnShardsFromLevel(config);
    resetSpaceLoot();
    // Clear the explosion VFX pool so a burst spawned in a prior space
    // session can't leak into the first render of the new scene.
    resetExplosionVfx();
    // DECISION: Scene constructs the ship rather than spaceState, so spaceState
    // can keep its `ShipEntity` import type-only and ShipEntity can safely
    // read `spaceState.cameraX` from its update() without a runtime cycle.
    // Spawn at left-third X / vertically centred (R-Type / Gradius convention).
    spaceState.ship = new ShipEntity(
      Math.floor(VIEWPORT_WIDTH / 3 - SHIP_WIDTH / 2),
      Math.floor(VIEWPORT_HEIGHT / 2 - SHIP_HEIGHT / 2),
    );
    // DECISION: Seed the HUD snapshot with the freshly-constructed ship's
    // HP and the new level name so SpaceHUD's first render shows real
    // values, not the 0/0/'' baseline `initSpaceState()` just wrote. The
    // first `stepSpace` tick will overwrite this on the next frame, but
    // the HUD's 100 ms RAF throttle would otherwise paint blanks during
    // the gap. (PR #124 review.)
    updateSpaceHudSnapshot(
      spaceState.ship.health,
      spaceState.ship.maxHealth,
      config.name,
    );
    resetAccumulator();
    setActiveStep(stepSpace);
    // DECISION: `runPhysicsFrame()` gates on `gameState.isRunning` for the
    // pause/driver check. Until that gate is unified across scenes (deferred —
    // would touch every non-platformer caller), SpaceScene must flip the same
    // flag PlayScene does, otherwise `stepSpace` would never tick.
    gameState.isRunning = true;
    this._active = true;
  }

  /**
   * Tear the scene down. Resets the active step to the platformer default so
   * the next scene starts from a known baseline.
   */
  public destroy(): void {
    if (!this._active) return;
    this._active = false;
    setActiveStep(null);
    gameState.isRunning = false;
    // Release pooled projectiles before clearing scene state so they're ready
    // for the next init() call.
    resetShipCombat();
    resetAsteroids();
    resetSpaceEnemies();
    resetWaveScheduler();
    resetBoss();
    releaseAllFighterDroneLasers();
    releaseAllBomberOrdnance();
    resetHomingMines();
    resetSpaceShards();
    resetSpaceLoot();
    resetExplosionVfx();
    clearSpaceState();
    clearAllInputState();
  }

  /** True between init() and destroy(). */
  public get isActive(): boolean {
    return this._active;
  }

  /**
   * Convenience accessor for the active ship (or null if no scene is running).
   * Lets render / HUD code read without importing `spaceState` directly.
   */
  public get ship(): typeof spaceState.ship {
    return spaceState.ship;
  }
}
